import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useRef } from 'react'

import { useLocaleContext } from '@src/components/locale/locale.context'
import { useI18nPreviewContext } from '@src/quince-site-common/src/contexts/i18n-preview-context/i18n-preview-context'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'

import { PageLoader } from '../pageLoader'
import { Skeleton } from '../skeleton'

import { getExpressCheckoutSrc } from './express-checkout.helpers'
import styles from './express-checkout.module.scss'

export interface ExpressCheckoutLegacyProps {
  showBothButtons: boolean
  showPaymentLoader?: boolean
  externalRef: React.MutableRefObject<HTMLIFrameElement | null>
  className?: string
  containerClass?: string
  showLoader?: boolean
}

function ExpressCheckoutLegacy({
  showBothButtons,
  showPaymentLoader = false,
  externalRef,
  className,
  containerClass,
  showLoader = false,
}: ExpressCheckoutLegacyProps) {
  const { t } = useTranslation('common')
  const { locale } = useLocaleContext()
  const deviceInfo = useDeviceInfo()
  const isI18nPreviewEnabled = useI18nPreviewContext()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const { src: iframeSrc } = getExpressCheckoutSrc(isI18nPreviewEnabled, showBothButtons, isMobile, locale)
  const ref = useRef<HTMLIFrameElement | null>(null)

  if (!iframeSrc) return null

  return (
    <div
      className={classNames(styles.expressCheckoutIframeContainer, containerClass)}
      data-dd-action-name="express checkout"
    >
      {showLoader && (
        <div className={styles.expressCheckoutLoaderContainer}>
          <Skeleton fullWidth height={isMobile ? 58 : 50} className={styles.expressCheckoutLoader} />
          {showBothButtons ? <Skeleton fullWidth height={50} className={styles.expressCheckoutLoader} /> : null}
        </div>
      )}
      <PageLoader
        showLoader={showPaymentLoader}
        title={t('payment_processing')}
        description={t('payment_processing_warning')}
      />
      <iframe
        title={t('express_checkout_iframe')}
        ref={el => {
          ref.current = el
          externalRef.current = el
        }}
        src={iframeSrc}
        className={classNames(styles.expressCheckoutIframe, className)}
        data-testid="express-checkout-iframe"
      />
    </div>
  )
}

export { ExpressCheckoutLegacy }
